import { type ChangeEvent, type Dispatch, type SetStateAction, memo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import { Icons, Input } from '@/js/libs/ui/src/V2';

interface DefaultSearchFilterProps {
  disabled?: boolean;
  intOnly?: boolean;
  filter?: string;
  placeholder?: string;
  saveFilter?: (value: string) => void;
  fullWidth?: boolean;
  dataTestId?: string;
  isLoading?: boolean;
}

interface SearchFilterWithDispatchProps extends DefaultSearchFilterProps {
  setFilter: Dispatch<SetStateAction<string>>;
  handleSetFilter?: never;
}

interface SearchFilterWithHandleDispatchProps extends DefaultSearchFilterProps {
  setFilter?: never;
  handleSetFilter: (e: ChangeEvent<HTMLInputElement>) => void;
}

type SearchFilterProps = SearchFilterWithDispatchProps | SearchFilterWithHandleDispatchProps;

export const SearchFilter = memo(function SearchFilter({
  disabled,
  intOnly,
  filter,
  setFilter,
  handleSetFilter,
  placeholder,
  saveFilter,
  fullWidth,
  dataTestId = 'search-field',
  isLoading = false,
}: SearchFilterProps) {
  const { colors } = useTheme();
  const isMobile = useMediaQuery({ query: Media.xsMax });
  const [t] = useTranslation(['backend', 'common']);

  const isChangeAllowed = useCallbackRef((e: ChangeEvent<HTMLInputElement>): boolean => {
    const { value } = e.currentTarget;
    if (intOnly && value.length > 0) {
      return /^\d+$/.test(value);
    }
    return true;
  });

  const onFilterChangeChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    if (isChangeAllowed(e)) {
      saveFilter?.(e.currentTarget.value);
      return setFilter?.(e.currentTarget.value) ?? handleSetFilter?.(e);
    }
    return null;
  });

  const onClear = useCallbackRef((e: MouseEvent<Element>) => {
    setFilter?.('');
    saveFilter?.('');
    handleSetFilter?.({
      ...e,
      target: {
        ...e.target,
        value: '',
      },
      currentTarget: {
        ...e.currentTarget,
        value: '',
      },
    } as unknown as ChangeEvent<HTMLInputElement>);
  });

  return (
    <Input
      disabled={disabled}
      width={fullWidth || isMobile ? '100%' : '220px'}
      placeholder={placeholder ?? t('common:PLACEHOLDER_SEARCH')}
      value={filter}
      onChange={onFilterChangeChange}
      icon={
        filter ? (
          <Icons.Clear filled size={20} color={colors.grey100_1} onClick={onClear} data-testid='icons-clear' />
        ) : (
          <Icons.Search size={20} color={colors.grey100_1} data-testid='icons-search' />
        )
      }
      isActionIcon={!!filter}
      dataTestId={dataTestId}
      isLoading={isLoading}
    />
  );
});
